// Import components for creating type aliases
import type { ComponentType, ReactNode } from 'react';

import type { components } from './api';

/**
 * Minimal context type for route guards that require authentication and roles.
 * Matches AuthState in useAuthStatus.ts.
 */
export interface RouteAuthContext {
  authentication: {
    status: 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'PENDING';
    user: { assumed_roles: Record<string, string[] | string> } | null;
  };
}

// Re-export the components interface and other API types
export type { components } from './api';
export * from './errors';

/**
 * Helper type to access API schemas more concisely
 * Usage: Schema<'OrgPublic'> instead of components['schemas']['OrgPublic']
 *
 * Note: Some API types use namespaced names (e.g., 'app__api__v1__studies__SortOrder')
 * to avoid conflicts between different API endpoints that have similar types.
 */
type Schema<T extends keyof components['schemas']> = components['schemas'][T];

// ============================================================================
// SAML Types

export type SAMLMetadata = Schema<'SAMLMetadata'>;
export type SAMLProviderGetResponse = Schema<'SAMLProviderGetResponse'>;

/**
 * Flat form defaults for SAML provider configuration UI
 */
export type SAMLProviderFormDefaults = {
  provider_name: string;
  email_domain: string;
  metadata_url?: string | null;
  metadata_file?: string | null;
  attr_email: string;
  attr_first_name: string;
  attr_last_name: string;
  attr_roles: string;
};

// ============================================================================
// Organization Types
// ============================================================================

export type Contact = Schema<'Contact'>;
export type Org = Schema<'OrgPublic'>;
export type OrgPublic = Schema<'OrgPublic'>;
export type OrgCreate = Schema<'OrgCreate'>;
export type OrgUpdate = Schema<'OrgUpdate'>;

/**
 * Organization type used for table display.
 * Alias of Org (OrgPublic) used by the Organizations table.
 */
export type OrgTableRow = Org;

// ============================================================================
// Role Types
// ============================================================================

/**
 * Role-related types and constants
 */
export type InternalRole = Schema<'Role'>;
export type AssumeRoleRequest = Schema<'AssumeRoleRequest'>;
export type AssumeRoleResponse = Schema<'AssumeRoleResponse'>;

// Centralized roles array for type safety and reuse
export const ROLES = ['SuperAdmin', 'OrgAdmin', 'DataOwner'] as const;
export const SUPER_ADMIN_ROLE = ROLES[0];
export const ORG_ADMIN_ROLE = ROLES[1];
export const DATA_OWNER_ROLE = ROLES[2];
export const BASIC_ROLES = [ORG_ADMIN_ROLE, DATA_OWNER_ROLE] as const;
export type Role = (typeof ROLES)[number];
// ============================================================================
// User Types
// ============================================================================

/**
 * User type with API Role
 */
export type User = Schema<'CurrentUser'>;

export type UserPublic = Schema<'UserPublic'>;
export type UserSearchRequest = Schema<'app__api__v1__users__UserSearchRequest__2'>;
export type UserSearchRequestGlobal = Schema<'app__api__v1__users__UserSearchRequest__1'>;
export type PaginatedUsersResponse = Schema<'PaginatedUsersResponse'>;
export type UserSortField = Schema<'UserSortField'>;
export type UserFilters = Schema<'UserFilters'>;
export type GrantUserOrgAccessRequest = Schema<'GrantUserOrgAccessRequest'>;
export type GrantUserOrgAccessResponse = Schema<'GrantUserOrgAccessResponse'>;
export type RevokeUserOrgAccessRequest = Schema<'RevokeUserOrgAccessRequest'>;
export type RevokeUserOrgAccessResponse = Schema<'RevokeUserOrgAccessResponse'>;

// ============================================================================
// Study Types
// ============================================================================

/**
 * StudyPublicGetOne with additional fields that were removed from the generated schema
 * but are still returned by the API and used in the UI.
 */
export type StudyPublicGetOne = Schema<'StudyPublicGetOne'> & {
  assays: StudyAssay[];
  num_sample_groups: number;
};
export type Study = StudyPublicGetOne;
export type StudyPublicGetMany = Schema<'StudyPublicGetMany'>;
export type StudyAssay = Schema<'StudyAssay'>;
export type PaginatedStudiesResponse = Schema<'PaginatedStudiesResponse'>;
export type StudyPermissions = Schema<'StudyPermissions'>;
export type StudyCreateResponse = Schema<'StudyCreateResponse'>;
// Override StudyUpdateResponse to use our augmented StudyPublicGetOne type
export type StudyUpdateResponse = { study: StudyPublicGetOne; message: string };
export type StudySearchRequest = Schema<'StudySearchRequest'>;
export type StudyFilters = Schema<'StudyFilters'>;
export type StudySortField = Schema<'StudySortField'>;

// Note: These types use namespaced schema names to avoid conflicts with other endpoints
export type SortOrder = Schema<'app__api__v1__studies__SortOrder'>;
export type TextFilterCondition = Schema<'app__api__v1__studies__TextFilterCondition'>;
export type DateFilterCondition = Schema<'app__api__v1__studies__DateFilterCondition'>;
export type LogicOperator = Schema<'app__api__v1__studies__LogicOperator'>;
export type FieldFilters = Schema<'app__api__v1__studies__FieldFilters'>;

// ============================================================================
// Instrument Types
// ============================================================================

export type InstrumentPublic = Schema<'InstrumentPublic'>;
export type InstrumentCreateRequest = Schema<'InstrumentCreateRequest'>;
export type InstrumentUpdateRequest = Schema<'InstrumentUpdateRequest'>;

// ============================================================================
// Instrument Run Types
// ============================================================================

export type InstrumentRunType = Schema<'InstrumentRunAssayType'>;
export type InstrumentRunAssayVersion = Schema<'InstrumentRunAssayVersion'>;
/**
 * InstrumentRunPublic with additional fields that were removed from the generated schema
 * but are still returned by the API and used in the UI.
 */
export type InstrumentRunPublic = Schema<'InstrumentRunPublic'> & {
  instrument_name?: string | null;
  started_at?: string | null;
  decode_status?: DecodeStatus | null;
  sample_qc?: QCStatus | null;
};

// Centralized instrument run assay version options for UI
// Both InstrumentRunType and InstrumentRunAssayVersion share the same values
export const INSTRUMENT_RUN_ASSAY_VERSIONS: InstrumentRunAssayVersion[] = ['Tau', 'Broadscale'] as const;

/**
 * Types for creating and updating instrument run drafts.
 *
 * The API generation produced names InstrumentRunDraftCreate / InstrumentRunDraftUpdate.
 * Map our alias types to those generated schema names for compatibility with existing code.
 */
export type InstrumentRunDraftCreate = Schema<'InstrumentRunDraftCreate'>;
export type InstrumentRunDraftUpdate = Schema<'InstrumentRunDraftUpdate'>;

export type PaginatedInstrumentRunsResponse = Schema<'PaginatedInstrumentRunsResponse'>;

export type InstrumentRunSpecies = Schema<'InstrumentRunSpecies'>;
export type InstrumentRunStatus = Schema<'InstrumentRunStatus'>;
export type QCStatus = Schema<'QCStatus'>;
export type DecodeStatus = Schema<'DecodeStatus'>;

// ============================================================================
// Sample Types
// ============================================================================

export type PaginatedSamplesResponse = Schema<'PaginatedSamplesResponse'>;
export type SampleWithNamesPublic = Schema<'SampleWithNamesPublic'>;
export type StudySample = Schema<'SampleWithNamesPublic'>;
export type OrgSampleFilters = Schema<'OrgSampleFilters'>;
export type SampleSearchRequest = Schema<'SampleSearchRequest'>;

export type SamplePublic = Schema<'SamplePublic'>;
export type Sample = Schema<'SamplePublic'>;
export type CSVParseResponse = Schema<'CSVParseResponse'>;
export type CSVSampleResponse = Schema<'CSVSampleResponse'>;

// ============================================================================
// Sample Proteoform Types
// ============================================================================

export type SampleProteoformPublic = Schema<'SampleProteoformPublic'>;
export type PaginatedSampleProteoformsResponse = Schema<'PaginatedSampleProteoformsResponse'>;
export type SampleProteoformSearchRequest = Schema<'SampleProteoformSearchRequest'>;
export type SampleProteoformFilters = Schema<'SampleProteoformFilters'>;
export type ProteoformSortField = Schema<'ProteoformSortField'>;

// ============================================================================
// Sample Proteoform Count Types
// ============================================================================

export type SampleProteoformCountPublic = Schema<'SampleProteoformCountPublic'>;
export type PaginatedSampleProteoformCountsResponse = Schema<'PaginatedSampleProteoformCountsResponse'>;
export type SampleProteoformCountSearchRequest = Schema<'SampleProteoformCountSearchRequest'>;
export type SampleProteoformCountFilters = Schema<'SampleProteoformCountFilters'>;
export type ProteoformCountSortField = Schema<'ProteoformCountSortField'>;
export type InstrumentRunSampleSize = Schema<'InstrumentRunSampleSize'>;
export type InstrumentRunConsumables = Schema<'InstrumentRunConsumables'>;

// ============================================================================

/**
 * Data row structure for instrument run sample table
 */
export type InstrumentRunDataRow = {
  name: string;
  study_name: string;
  org_name?: string;
  org_id?: string;
  well_position: string;
  demux_index: number;
  is_control?: boolean | null;
  control_type?: string | null;
  flow_cell?: string | null;
  lane?: number | null;
  library_group?: string | null;
  sample_group?: string | null;
};

/**
 * Form values for instrument run creation form
 */
export type InstrumentRunFormValues = {
  assay_version: InstrumentRunAssayVersion;
  description?: string;
  name: string;
  noOfSamples: string;
  samples: InstrumentRunDataRow[];
  species: InstrumentRunSpecies;
  useTauLibPrepControl2?: boolean;
  consumables?: Partial<InstrumentRunConsumables>;
};

/**
 * Minimal structural type for a TanStack Form field render-prop argument.
 * Covers the subset of FieldApi used across InstrumentRunHeader and related components.
 * TanStack Form's actual FieldApi has 23+ generics — this avoids threading them through
 * component boundaries while still providing type safety for the values we access.
 */
export type FormFieldApi<TValue = unknown> = {
  state: { value: TValue };
  handleChange: (value: TValue) => void;
};

/**
 * Type for the instrument run form instance
 * This can be passed to child components that need access to the form API
 *
 * We use a flexible type that is compatible with TanStack Form's complex generic signature
 * while still providing type safety for the values.
 *
 * Note: We use Record<string, unknown> instead of 'any' to maintain type safety
 * while allowing additional properties from the TanStack Form API.
 */
export type InstrumentRunForm = Record<string, unknown> & {
  handleSubmit?: () => Promise<void>;
  reset?: () => void;
  setFieldValue?: (field: string, value: unknown) => void;
  state?: {
    values?: InstrumentRunFormValues;
    isSubmitting?: boolean;
    canSubmit?: boolean;
  };
  Subscribe?: ComponentType<unknown>;
  Field?: ComponentType<{ name: string; children: (field: FormFieldApi<unknown>) => ReactNode }>;
};
